/**
 * M10 — Platform Admin oldal
 * Csak is_platform_admin=true JWT-vel érhető el.
 */
import { useState, useEffect, useCallback } from 'react';
import { get, post, patch } from '../../lib/api';
import { PageHeader, Button, Badge, Card } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';

// ── Típusok ────────────────────────────────────────────────────────────────────

interface TenantRow {
  id: string; name: string; created_at: string; setup_state: string;
  plan_code: string; billing_cycle: string; sub_status: string;
  trial_ends_at: string | null; current_period_ends_at: string | null;
  base_price_filler: number; has_payment_method: boolean; user_count: number;
}

interface Metrics {
  active_tenants: number; trial_tenants: number;
  at_risk_tenants: number; churned_30d: number; mrr_huf: number;
}

// ── Segédek ────────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  active: 'success', trial: 'info', past_due: 'warning',
  grace: 'warning', read_only: 'warning', locked: 'error',
  cancelled: 'error', ended: 'error',
};

function fmtHuf(filler: number) {
  return (filler / 100).toLocaleString('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 });
}
function fmtDate(d: string | null) {
  return d ? new Date(d).toLocaleDateString('hu-HU') : '—';
}

// ── Metrika kártyák ────────────────────────────────────────────────────────────

function MetricsSection({ metrics }: { metrics: Metrics }) {
  const cards = [
    { label: 'Aktív tenant', value: metrics.active_tenants, color: 'var(--success)' },
    { label: 'Trial', value: metrics.trial_tenants, color: 'var(--info, #3b82f6)' },
    { label: 'Veszélyes', value: metrics.at_risk_tenants, color: 'var(--warning)' },
    { label: 'Churn (30 nap)', value: metrics.churned_30d, color: 'var(--error)' },
    { label: 'MRR', value: fmtHuf(Number(metrics.mrr_huf) * 100), color: 'var(--navy)' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
      {cards.map(c => (
        <Card key={c.label} style={{ padding: '14px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{c.label}</div>
        </Card>
      ))}
    </div>
  );
}

// ── Tenant lista ───────────────────────────────────────────────────────────────

function TenantList() {
  const { toast } = useToast();
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [editTenant, setEditTenant] = useState<TenantRow | null>(null);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (q) params.set('search', q);
      const r = await get<{ rows: TenantRow[]; total: number }>(`/platform-admin/tenants?${params}`);
      setTenants(r.rows);
      setTotal(r.total);
    } catch { toast('Betöltési hiba', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleImpersonate(tenantId: string) {
    setImpersonating(tenantId);
    try {
      const r = await post<{ token: string; targetUser: { name: string } }>(`/platform-admin/impersonate/${tenantId}`, {});
      // Impersonation token tárolása — az oldal újratöltésével lép érvénybe
      const orig = localStorage.getItem('accessToken');
      if (orig) localStorage.setItem('accessToken_pre_impersonation', orig);
      localStorage.setItem('accessToken', r.token);
      toast(`Impersonation aktív: ${r.targetUser.name}`, 'info');
      window.location.href = '/';
    } catch (e) { toast((e as Error).message, 'error'); }
    finally { setImpersonating(null); }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input
          placeholder="Keresés tenant névre..."
          value={search}
          onChange={e => { setSearch(e.target.value); load(e.target.value); }}
          style={{ padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)', fontSize: 13, width: 260 }}
        />
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-secondary)', alignSelf: 'center' }}>
          {total} tenant
        </span>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Tenant', 'Csomag', 'Állapot', 'Trial vége', 'MRR', 'Felhasználók', 'Műveletek'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{t.name}</td>
                  <td style={{ padding: '8px 10px' }}>
                    {t.plan_code?.toUpperCase() || '—'} / {t.billing_cycle === 'yearly' ? 'éves' : 'havi'}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <Badge color={STATUS_COLOR[t.sub_status] || 'info'}>{t.sub_status || '—'}</Badge>
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDate(t.trial_ends_at)}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    {t.base_price_filler ? fmtHuf(t.base_price_filler) : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{t.user_count}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button size="sm" variant="secondary" onClick={() => setEditTenant(t)}>Szerkesztés</Button>
                      <Button size="sm" variant="ghost"
                        disabled={impersonating === t.id}
                        onClick={() => handleImpersonate(t.id)}
                      >
                        {impersonating === t.id ? '...' : 'Belépés'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editTenant && (
        <EditSubscriptionModal
          tenant={editTenant}
          onClose={() => setEditTenant(null)}
          onSaved={() => { setEditTenant(null); load(search); toast('Módosítás elmentve.', 'success'); }}
        />
      )}
    </div>
  );
}

// ── Előfizetés szerkesztés modal ───────────────────────────────────────────────

function EditSubscriptionModal({ tenant, onClose, onSaved }: { tenant: TenantRow; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [plan, setPlan] = useState(tenant.plan_code || 'start');
  const [cycle, setCycle] = useState(tenant.billing_cycle || 'monthly');
  const [status, setStatus] = useState(tenant.sub_status || 'active');
  const [trialEnd, setTrialEnd] = useState(tenant.trial_ends_at?.slice(0, 10) || '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await patch(`/platform-admin/tenants/${tenant.id}/subscription`, {
        planCode: plan, billingCycle: cycle, status,
        ...(trialEnd ? { trialEndsAt: trialEnd } : {}),
      });
      onSaved();
    } catch (e) { toast((e as Error).message, 'error'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--paper)', borderRadius: 12, padding: 24, width: 380, boxShadow: '0 20px 40px rgba(0,0,0,.3)' }}>
        <h3 style={{ margin: '0 0 16px' }}>{tenant.name} — előfizetés</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Csomag
            <select value={plan} onChange={e => setPlan(e.target.value)} style={selectStyle}>
              <option value="start">Start</option>
              <option value="standard">Standard</option>
              <option value="pro">Pro</option>
            </select>
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Számlázási ciklus
            <select value={cycle} onChange={e => setCycle(e.target.value)} style={selectStyle}>
              <option value="monthly">Havi</option>
              <option value="yearly">Éves</option>
            </select>
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Állapot
            <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
              {['trial','active','past_due','grace','read_only','locked','cancelled'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Trial vége
            <input type="date" value={trialEnd} onChange={e => setTrialEnd(e.target.value)} style={selectStyle} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <Button onClick={handleSave} disabled={saving} loading={saving}>Mentés</Button>
          <Button variant="secondary" onClick={onClose}>Mégse</Button>
        </div>
      </div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  display: 'block', width: '100%', marginTop: 4,
  padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)', fontSize: 13,
};

// ── Impersonation napló ────────────────────────────────────────────────────────

function ImpersonationLog() {
  const { toast } = useToast();
  const [log, setLog] = useState<Array<{ id: string; admin_email: string; target_tenant_name: string; action: string; created_at: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get<typeof log>('/platform-admin/impersonation-log')
      .then(setLog)
      .catch(() => toast('Napló betöltési hiba', 'error'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>Betöltés...</div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)' }}>
            {['Időpont', 'Admin', 'Tenant', 'Művelet'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {log.map(row => (
            <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleString('hu-HU')}</td>
              <td style={{ padding: '7px 10px' }}>{row.admin_email}</td>
              <td style={{ padding: '7px 10px' }}>{row.target_tenant_name}</td>
              <td style={{ padding: '7px 10px' }}>
                <Badge color={row.action === 'start' ? 'warning' : 'success'}>{row.action === 'start' ? 'Belépés' : 'Kilépés'}</Badge>
              </td>
            </tr>
          ))}
          {log.length === 0 && (
            <tr><td colSpan={4} style={{ padding: '24px 10px', textAlign: 'center', color: 'var(--text-secondary)' }}>Nincs napló.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Fő oldal ───────────────────────────────────────────────────────────────────

export default function PlatformAdminPage() {
  const { isPlatformAdmin } = useAuth();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [tab, setTab] = useState<'tenants' | 'log'>('tenants');
  if (!isPlatformAdmin) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <p>Platform-admin hozzáférés szükséges.</p>
      </div>
    );
  }

  useEffect(() => {
    get<Metrics>('/platform-admin/metrics').then(setMetrics).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader title="Platform Admin" subtitle="Modulum belső kezelőfelület — platform operátoroknak" />

      {metrics && <MetricsSection metrics={metrics} />}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button size="sm" variant={tab === 'tenants' ? 'primary' : 'secondary'} onClick={() => setTab('tenants')}>
          Tenantok
        </Button>
        <Button size="sm" variant={tab === 'log' ? 'primary' : 'secondary'} onClick={() => setTab('log')}>
          Impersonation napló
        </Button>
      </div>

      {tab === 'tenants' && <TenantList />}
      {tab === 'log' && <ImpersonationLog />}
    </div>
  );
}
